import {
    ArrowLeftOutlined,
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    ScissorOutlined
} from "@ant-design/icons";
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Switch } from "antd";
import TextArea from "antd/lib/input/TextArea";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { PatientRequestDto, PatientResponseDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import {
    clearSavePatientError,
    detachPatient,
    fetchPatient,
    initPatientState,
    updatePatient,
    updatePatientByCode,
    updatePatientFields
} from "../features/patient/patientSlice";
import { deletePatient, fetchPatients } from "../features/patient/patientsSlice";
import { RouteNames } from "../routes";
import { useTranslate } from "../i18n";
import { getErrorMessage } from "../utils/getErrorMessage";
import { AddPatientByCode } from "../components/AddPatientByCode";

const { Option } = Select;

export const EditPatientPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const t = useTranslate();
    const patientsStatus = useAppSelector((state: RootState) => state.patients.status);
    const patient = useAppSelector((state: RootState) => state.patient.patient);
    const status = useAppSelector((state: RootState) => state.patient.status);
    const error = useAppSelector((state: RootState) => state.patient.error);
    const countries = useAppSelector((state: RootState) => state.country.countries);
    const patientId = params.patientId;
    const isNew = patientId === undefined || patientId === "new";
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isRemovePatientWarningVisible, setIsRemovePatientWarningVisible] = useState(false);
    const [isDeletePatientWarningVisible, setIsDeletePatientWarningVisible] = useState(false);

    useEffect(() => {

        if (patientsStatus === "idle") {
            dispatch(fetchPatients())
        }
        dispatch(fetchPatient(patientId!))


    }, []);

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchPatients());
            dispatch(initPatientState());
            navigate(`/${RouteNames.patients}`);
        }
        if (status === "auth-error" || status === "error") {
            setIsModalVisible(true);
        }
    }, [status])


    const onCancel = () => {
        navigate(`/${RouteNames.patients}`);
    }

    const changePatient = (patient: PatientRequestDto) => {
        dispatch(updatePatientFields(patient));
    }

    const onSave = () => {
        dispatch(updatePatient(patient as PatientRequestDto));
    }


    const onDelete = () => {
        setIsDeletePatientWarningVisible(true);
    }

    const onRemove = () => {
        setIsRemovePatientWarningVisible(true);
    }

    const onDeletePatient = () => {
        if (patient?.id != null) {
            dispatch(deletePatient(patient.id));
        }
    }

    const onRemovePatient = () => {
        if (patient?.id != null) {
            dispatch(detachPatient(patient.id));
        }
    }

    const onConfirm = (patient: PatientResponseDto) => {
        dispatch(updatePatientByCode(patient));
    }

    const handleAuthOk = () => {
        setIsModalVisible(false)
        navigate("/")
    }

    const handleErrorOk = () => {
        setIsModalVisible(false);
        dispatch(clearSavePatientError());
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }


    if (status === "idle" || status === "loading" || patientsStatus === "idle") {
        return <>
            <PageHeader eyebrow={t("nav.patients")} title={t(isNew ? "patient.new" : "patient.one")} />
            <TableSkeleton />
        </>;
    }

    if (status === "auth-error") {
        return <Modal
            title={t("auth.failed.title")}
            visible={isModalVisible}
            onOk={handleAuthOk}
            okText={t("common.signIn")}
            onCancel={handleCancel}
            cancelButtonProps={{ hidden: true }}
        >
            <p>{t("auth.failed.body")}</p>
        </Modal>
    }

    if (status === "error") {
        const errorMessage = getErrorMessage(error, t);
        return <Modal
            title={errorMessage.title}
            visible={isModalVisible}
            onOk={handleErrorOk}
            onCancel={handleCancel}
            cancelButtonProps={{ hidden: true }}
        >
            <p>{errorMessage.details}</p>
        </Modal>
    }

    if (status === "saved") {
        return (<div></div>);
    }

    if (patient?.userAccount?.mobileNumber === undefined) {
        return <AddPatientByCode onConfirm={onConfirm} />
    }

    const patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ");

    return (<>
        <PageHeader
            eyebrow={t("nav.patients")}
            title={isNew ? t("patient.new") : (patientName || t("patient.one"))}
            actions={
                <Button icon={<ArrowLeftOutlined />} onClick={onCancel}>{t("common.back")}</Button>
            }
        />

        <div className='edit-person-main-frame'>
            <Form className='person-form'
                name="basic"
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
                initialValues={{ remember: true }}
                autoComplete="off"
            >
                <div className="person-form__actions">
                    <Button className="btn-warning" icon={<ScissorOutlined />} onClick={onRemove}
                        disabled={patient.id === undefined}>{t("common.remove")}</Button>
                    <Button danger icon={<DeleteOutlined />} onClick={onDelete}
                        disabled={patient.id === undefined}>{t("common.delete")}</Button>
                    <Button icon={<CloseOutlined />} onClick={onCancel}>{t("common.cancel")}</Button>
                    <Button icon={<CheckOutlined />} type='primary' onClick={onSave}>{t("common.save")}</Button>
                </div>

                <Form.Item label={t("person.field.phone")} rules={[{ required: true }]}
                    initialValue={patient?.userAccount?.mobileNumber}>
                    <Input placeholder={t("person.placeholder.phone")}
                        defaultValue={patient?.userAccount?.mobileNumber || ""}
                        onChange={(e) => {
                            changePatient({ ...patient, userAccount: { ...patient.userAccount, mobileNumber: e.target.value } } as PatientRequestDto)
                        }} />
                </Form.Item>
                <Form.Item label={t("person.field.email")}>
                    <Input disabled placeholder={t("person.placeholder.email")}
                        defaultValue={patient?.userAccount?.emailAddress || ""}
                    />
                </Form.Item>
                <Form.Item label={t("person.field.lastName")}>
                    <Input placeholder={t("person.placeholder.lastName")} defaultValue={patient.lastName || ""} onChange={(e) => { changePatient({ ...patient, lastName: e.target.value } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.gender")}>
                    <Select defaultValue={patient.gender} onChange={(e) => { changePatient({ ...patient, gender: e } as PatientRequestDto) }} style={{ width: 140 }}>
                        <Option value="Male">{t("person.gender.male")}</Option>
                        <Option value="Female">{t("person.gender.female")}</Option>
                        <Option value="Other">{t("person.gender.other")}</Option>
                    </Select>
                </Form.Item>
                <Form.Item label={t("person.field.birthday")}>
                    <DatePicker defaultValue={patient.birthday != undefined ? moment(patient.birthday, "YYYY-MM-DD").utc() : moment().utc()} onChange={(e) => { changePatient({ ...patient, birthday: e?.format("") } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.country")} initialValue={patient.address?.country}>
                    <Select
                        onChange={(e) => changePatient({ ...patient, address: { ...patient.address, alpha3Code: e } } as PatientRequestDto)}
                        defaultValue={patient.address?.alpha3Code}
                        showSearch
                        placeholder={t("person.placeholder.country")}
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            return (option!.children![2] as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }}
                        filterSort={(optionA, optionB) =>
                            (optionA!.children![2] as unknown as string)
                                .toLowerCase()
                                .localeCompare((optionB!.children![2] as unknown as string).toLowerCase())
                        }
                    >
                        {countries.map(country => <Option key={country.alpha3Code} value={country.alpha3Code}><img src={country.flag!} alt={country.alpha3Code!} width="20"></img> {country.name}</Option>)}

                    </Select>
                </Form.Item>
                <Form.Item label={t("person.field.address")}>
                    <Input defaultValue={patient.address?.streetAddress1 as string | undefined} placeholder={t("person.placeholder.address")} onChange={(e) => { changePatient({ ...patient, address: { ...patient.address, streetAddress1: e.target.value } } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.address2")}>
                    <Input defaultValue={patient.address?.streetAddress2 as string | undefined} placeholder={t("person.placeholder.address2")} onChange={(e) => { changePatient({ ...patient, address: { ...patient.address, streetAddress2: e.target.value } } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.city")}>
                    <Input defaultValue={patient.address?.city as string | undefined} placeholder={t("person.placeholder.city")} onChange={(e) => { changePatient({ ...patient, address: { ...patient.address, city: e.target.value } } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.state")}>
                    <Input defaultValue={patient.address?.state as string | undefined} placeholder={t("person.placeholder.state")} onChange={(e) => { changePatient({ ...patient, address: { ...patient.address, state: e.target.value } } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.postcode")}>
                    <Input defaultValue={patient.address?.postCode as string | undefined} placeholder={t("person.placeholder.postcode")} onChange={(e) => { changePatient({ ...patient, address: { ...patient.address, postCode: e.target.value } } as PatientRequestDto) }} />
                </Form.Item>

                <Form.Item label={t("person.field.allowedDistance")} extra={t("patient.hint.allowedDistance")}>
                    <InputNumber min={0} max={1000} defaultValue={patient.allowedDistance as number | undefined} onChange={(e) => { changePatient({ ...patient, allowedDistance: e } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.checkAllowedDistance")}>
                    <Switch checked={patient.checkAllowedDistance} onChange={(e) => { changePatient({ ...patient, checkAllowedDistance: e } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.doctorFirstName")}>
                    <Input defaultValue={patient.doctorName as string | undefined} placeholder={t("person.placeholder.doctorFirstName")} onChange={(e) => { changePatient({ ...patient, doctorName: e.target.value } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.doctorLastName")}>
                    <Input defaultValue={patient.doctorLastName as string | undefined} placeholder={t("person.placeholder.doctorLastName")} onChange={(e) => { changePatient({ ...patient, doctorLastName: e.target.value } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.doctorPhone")} initialValue={patient.doctorPhone}>
                    <Input defaultValue={patient.doctorPhone as string | undefined} placeholder={t("person.placeholder.doctorPhone")} onChange={(e) => { changePatient({ ...patient, doctorPhone: e.target.value } as PatientRequestDto) }} />
                </Form.Item>
                <Form.Item label={t("person.field.diagnosis")} initialValue={patient.diagnosis}>
                    <TextArea defaultValue={patient.diagnosis as string | undefined} rows={8} placeholder={t("person.placeholder.diagnosis")} onChange={(e) => { changePatient({ ...patient, diagnosis: e.target.value } as PatientRequestDto) }} />
                </Form.Item>
            </Form>
        </div>

        <ConfirmDialog
            visible={isRemovePatientWarningVisible}
            tone="warning"
            title={t("confirm.remove.title")}
            body={t("confirm.remove.patient", { name: patientName })}
            confirmLabel={t("common.remove")}
            onConfirm={onRemovePatient}
            onCancel={() => setIsRemovePatientWarningVisible(false)}
        />
        <ConfirmDialog
            visible={isDeletePatientWarningVisible}
            title={t("confirm.delete.title")}
            body={t("confirm.delete.patient", { name: patientName })}
            confirmLabel={t("common.delete")}
            onConfirm={onDeletePatient}
            onCancel={() => setIsDeletePatientWarningVisible(false)}
        />
    </>);
}
